'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../config/api-config';
import axios from '../utils/axios-config';
import { toast } from 'sonner';

export function useCategories() {
  const queryClient = useQueryClient();

  // Get all categories
  const useGetAllCategories = () => {
    return useQuery({
      queryKey: ['categories'],
      queryFn: async () => {
        const response = await axios.get(API_ENDPOINTS.CATEGORIES.GET_ALL);
        return response.data.categories || [];
      },
      staleTime: 1000 * 60 * 500000, // 5 minutes
    });
  };

  // Add category mutation (supports single category or array)
  const addCategoryMutation = useMutation({
    mutationFn: async (categoryData) => {
      const response = await axios.post(
        API_ENDPOINTS.CATEGORIES.ADD_CATEGORY,
        categoryData
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });

  // Add pinned message mutation
  const addPinnedMessageMutation = useMutation({
    mutationFn: async ({ categoryId, messageData }) => {
      const response = await axios.post(
        API_ENDPOINTS.CATEGORIES.ADD_PINNED_MESSAGE.replace(
          ':categoryId',
          categoryId
        ),
        messageData
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({
        queryKey: ['category', variables.categoryId],
      });
    },
  });

  // Change rules mutation
  const changeRulesMutation = useMutation({
    mutationFn: async ({ categoryId, rules }) => {
      const response = await axios.put(
        API_ENDPOINTS.CATEGORIES.CHANGE_RULES.replace(
          ':categoryId',
          categoryId
        ),
        { rules }
      );
      return response.data;
    },
    onMutate: async ({ categoryId, rules }) => {
      await queryClient.cancelQueries();

      // Optimistically update the category in the categories list
      const previousCategories = queryClient.getQueryData(['categories']);
      if (Array.isArray(previousCategories)) {
        queryClient.setQueryData(
          ['categories'],
          previousCategories.map((category) =>
            category.id === categoryId ? { ...category, rules } : category
          )
        );
      }

      // Optimistically update single category if cached
      const previousCategory = queryClient.getQueryData([
        'category',
        categoryId,
      ]);
      if (previousCategory) {
        queryClient.setQueryData(['category', categoryId], {
          ...previousCategory,
          rules,
        });
      }

      return { previousCategories, previousCategory };
    },
    onError: (err, variables, context) => {
      if (context?.previousCategories) {
        queryClient.setQueryData(['categories'], context.previousCategories);
      }
      if (context?.previousCategory) {
        queryClient.setQueryData(
          ['category', variables.categoryId],
          context.previousCategory
        );
      }
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({
        queryKey: ['category', variables.categoryId],
      });
    },
  });

  // Set lock mutation
  const setLockMutation = useMutation({
    mutationFn: async ({ categoryId, isLocked }) => {
      const response = await axios.put(
        API_ENDPOINTS.CATEGORIES.SET_LOCK.replace(':categoryId', categoryId),
        { isLocked }
      );
      return response.data;
    },
    onMutate: async ({ categoryId, isLocked }) => {
      await queryClient.cancelQueries();

      // Optimistically update the category in the categories list
      const previousCategories = queryClient.getQueryData(['categories']);
      if (Array.isArray(previousCategories)) {
        queryClient.setQueryData(
          ['categories'],
          previousCategories.map((category) =>
            category.id === categoryId ? { ...category, isLocked } : category
          )
        );
      }

      // Optimistically update single category if cached
      const previousCategory = queryClient.getQueryData([
        'category',
        categoryId,
      ]);
      if (previousCategory) {
        queryClient.setQueryData(['category', categoryId], {
          ...previousCategory,
          isLocked,
        });
      }

      return { previousCategories, previousCategory };
    },
    onError: (err, variables, context) => {
      if (context?.previousCategories) {
        queryClient.setQueryData(['categories'], context.previousCategories);
      }
      if (context?.previousCategory) {
        queryClient.setQueryData(
          ['category', variables.categoryId],
          context.previousCategory
        );
      }
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({
        queryKey: ['category', variables.categoryId],
      });
    },
  });

  // Change name mutation
  const changeNameMutation = useMutation({
    mutationFn: async ({ categoryId, name }) => {
      const response = await axios.put(
        API_ENDPOINTS.CATEGORIES.CHANGE_NAME.replace(':categoryId', categoryId),
        { name }
      );
      return response.data;
    },
    onMutate: async ({ categoryId, name }) => {
      await queryClient.cancelQueries();

      // Optimistically update the category in the categories list
      const previousCategories = queryClient.getQueryData(['categories']);
      if (Array.isArray(previousCategories)) {
        queryClient.setQueryData(
          ['categories'],
          previousCategories.map((category) =>
            category.id === categoryId ? { ...category, name } : category
          )
        );
      }

      // Optimistically update single category if cached
      const previousCategory = queryClient.getQueryData([
        'category',
        categoryId,
      ]);
      if (previousCategory) {
        queryClient.setQueryData(['category', categoryId], {
          ...previousCategory,
          name,
        });
      }

      return { previousCategories, previousCategory };
    },
    onError: (err, variables, context) => {
      if (context?.previousCategories) {
        queryClient.setQueryData(['categories'], context.previousCategories);
      }
      if (context?.previousCategory) {
        queryClient.setQueryData(
          ['category', variables.categoryId],
          context.previousCategory
        );
      }
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({
        queryKey: ['category', variables.categoryId],
      });
    },
  });

  // Pin discussion mutation
  const pinDiscussionMutation = useMutation({
    mutationFn: async ({ categoryId, name, discussionOid }) => {
      if (!categoryId || !name || !discussionOid) {
        throw new Error('categoryId, name, and discussionOid are required');
      }
      if (
        typeof categoryId !== 'string' ||
        typeof name !== 'string' ||
        typeof discussionOid !== 'string'
      ) {
        throw new Error('categoryId, name, and discussionOid must be strings');
      }
      const response = await axios.put(
        API_ENDPOINTS.CATEGORIES.PIN_DISCUSSION,
        { categoryId, name, discussionOid }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      toast.success('Discussion pinned successfully!');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({
        queryKey: ['category', variables.categoryId],
      });
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussion'] });
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussions'] });
    },
    onError: (error) => {
      toast.error('Failed to pin discussion.');
    },
  });

  // Unpin discussion mutation
  const unpinDiscussionMutation = useMutation({
    mutationFn: async ({ categoryId, name, discussionOid }) => {
      if (!categoryId || !name || !discussionOid) {
        throw new Error('categoryId, name, and discussionOid are required');
      }
      if (
        typeof categoryId !== 'string' ||
        typeof name !== 'string' ||
        typeof discussionOid !== 'string'
      ) {
        throw new Error('categoryId, name, and discussionOid must be strings');
      }
      const response = await axios.put(
        API_ENDPOINTS.CATEGORIES.UNPIN_DISCUSSION,
        { categoryId, name, discussionOid }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      toast.success('Discussion unpinned successfully!');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({
        queryKey: ['category', variables.categoryId],
      });
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussion'] });
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussions'] });
    },
    onError: (error) => {
      toast.error('Failed to unpin discussion.');
    },
  });

  // Freeze discussion mutation (only accepts discussionOid)
  const freezeDiscussionMutation = useMutation({
    mutationFn: async ({ discussionOid }) => {
      console.log('[FREEZE DISCUSSION SUBMIT]', { discussionOid });
      if (!discussionOid) {
        throw new Error('discussionOid is required');
      }
      if (typeof discussionOid !== 'string') {
        throw new Error('discussionOid must be a string');
      }
      const response = await axios.put(
        API_ENDPOINTS.CATEGORIES.FREEZE_DISCUSSION,
        { discussionOid }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Discussion frozen successfully!');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussion'] });
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussions'] });
    },
    onError: (error) => {
      toast.error('Failed to freeze discussion.');
    },
  });

  // Unfreeze discussion mutation (only accepts discussionOid)
  const unfreezeDiscussionMutation = useMutation({
    mutationFn: async ({ discussionOid }) => {
      console.log('[UNFREEZE DISCUSSION SUBMIT]', { discussionOid });
      if (!discussionOid) {
        throw new Error('discussionOid is required');
      }
      if (typeof discussionOid !== 'string') {
        throw new Error('discussionOid must be a string');
      }
      const response = await axios.put(
        API_ENDPOINTS.CATEGORIES.UNFREEZE_DISCUSSION,
        { discussionOid }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Discussion unfrozen successfully!');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussion'] });
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussions'] });
    },
    onError: (error) => {
      toast.error('Failed to unfreeze discussion.');
    },
  });

  // Get single category (if you need it)
  const useGetCategoryById = (categoryId) => {
    return useQuery({
      queryKey: ['category', categoryId],
      queryFn: async () => {
        // If you have a specific endpoint for getting a single category, use it here
        // Otherwise, we can get it from the all categories query
        const allCategories = queryClient.getQueryData(['categories']);
        if (allCategories) {
          return allCategories.find((cat) => cat.id === categoryId) || null;
        }

        // Fallback: fetch all categories and find the one we need
        const response = await axios.get(API_ENDPOINTS.CATEGORIES.GET_ALL);
        const categories = response.data.categories || [];
        return categories.find((cat) => cat.id === categoryId) || null;
      },
      enabled: !!categoryId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    });
  };

  return {
    // Query hooks
    useGetAllCategories,
    useGetCategoryById,

    // Mutation hooks
    addCategory: addCategoryMutation,
    addPinnedMessage: addPinnedMessageMutation,
    changeRules: changeRulesMutation,
    setLock: setLockMutation,
    changeName: changeNameMutation,
    pinDiscussion: pinDiscussionMutation,
    unpinDiscussion: unpinDiscussionMutation,
    freezeDiscussion: freezeDiscussionMutation,
    unfreezeDiscussion: unfreezeDiscussionMutation,

    // Factory functions (for consistency with other hooks)
    addCategoryMutation: () => addCategoryMutation,
    addPinnedMessageMutation: () => addPinnedMessageMutation,
    changeRulesMutation: () => changeRulesMutation,
    setLockMutation: () => setLockMutation,
    changeNameMutation: () => changeNameMutation,
    pinDiscussionMutation: () => pinDiscussionMutation,
    unpinDiscussionMutation: () => unpinDiscussionMutation,
    freezeDiscussionMutation: () => freezeDiscussionMutation,
    unfreezeDiscussionMutation: () => unfreezeDiscussionMutation,
  };
}

// Standalone hook for getting all categories (for convenience)
export function useGetAllCategories() {
  const categories = useCategories();
  return categories.useGetAllCategories();
}

// Standalone hook for getting category by ID (for convenience)
export function useGetCategoryById(categoryId) {
  const categories = useCategories();
  return categories.useGetCategoryById(categoryId);
}
